import React, { useState, useEffect } from 'react'
import {
	Table,
	TableRow,
	TableCell,
	TableBody,
	TableHead,
	TextField,
	Checkbox,
	Button,
	Select,
	MenuItem,
} from '@material-ui/core'
import Loading from '../../generic/Loading'
import axios from 'axios'
import { getApiPath } from '../../data/api'
import { getModel } from '../../data/Model'
import { counterpartyTypes } from '../../lib/counterpartyTypes'
import { markets } from '../../lib/markets'
import {
	validateEmail,
	validatePhone,
	dedupStrings,
} from '../../lib/contactValidation'

export default function ValidateCasesDialog({
	open,
	onClose,
	cases,
	loading,
	error,
	clientName,
	refetchCases,
}) {
	const [editedCases, setEditedCases] = useState([])
	const [originalCases, setOriginalCases] = useState([])
	const [saving, setSaving] = useState(false)
	const [saveError, setSaveError] = useState(null)
	const [saveSuccess, setSaveSuccess] = useState(null)
	const [calculatingPricing, setCalculatingPricing] = useState(false)

	useEffect(() => {
		setSaveError(null)
		setSaveSuccess(null)
	}, [open])

	useEffect(() => {
		if (cases && cases.length > 0) {
			const clonedCases = JSON.parse(JSON.stringify(cases)).map((c) => ({
				...c,
				emails: Array.isArray(c.emails) ? c.emails : [],
				phones: Array.isArray(c.phones) ? c.phones : [],
			}))
			setEditedCases(clonedCases)
			setOriginalCases(clonedCases)
		}
	}, [cases])

	const handleCellChange = (violationId, field, value) => {
		setEditedCases((prev) => {
			const isOrgField = ORG_FIELDS.has(field)
			let matches
			if (isOrgField) {
				const target = prev.find((c) => c.violation_id === violationId)
				if (!target) return prev
				const orgKey = getOrgKey(target)
				matches = (c) => getOrgKey(c) === orgKey
			} else {
				matches = (c) => c.violation_id === violationId
			}

			return prev.map((c) => {
				if (!matches(c)) return c
				return applyFieldUpdate(c, field, value)
			})
		})
	}

	const applyFieldUpdate = (c, field, value) => {
		if (ARRAY_FIELDS.has(field)) {
			return { ...c, [field]: Array.isArray(value) ? value : [] }
		}
		if (BOOLEAN_FIELDS.has(field)) {
			return { ...c, [field]: !!value }
		}
		if (NUMERIC_FIELDS.has(field)) {
			if (value === '' || value === null || value === undefined) {
				return { ...c, [field]: null }
			}
			const numValue = Number(value)
			return { ...c, [field]: isNaN(numValue) ? null : numValue }
		}
		return { ...c, [field]: value || null }
	}

	const handleMarkAsLost = async (violationId) => {
		if (!window.confirm('Are you sure you want to mark this case as lost?')) {
			return
		}

		try {
			const url = getApiPath(
				`/violations/${violationId}/state?auth_token=${getModel().auth_token}`,
			)
			await axios.patch(url, { state: 'pipedrive_lost' })

			// Remove the case from the list
			setEditedCases((prev) =>
				prev.filter((c) => c.violation_id !== violationId),
			)
			setOriginalCases((prev) =>
				prev.filter((c) => c.violation_id !== violationId),
			)
		} catch (error) {
			console.error('Failed to mark case as lost:', error)
			window.alert('Failed to mark case as lost. Please try again.')
		}
	}

	const handleCalculatePricing = async () => {
		if (
			!window.confirm(
				`Are you sure you want to recalculate pricing for all ${editedCases.length} cases? This will overwrite existing pricing data.`,
			)
		) {
			return
		}

		setCalculatingPricing(true)
		setSaveError(null)
		setSaveSuccess(null)

		try {
			const errors = []
			let successCount = 0

			for (const caseItem of editedCases) {
				try {
					const url = getApiPath(
						`/pricing/violations/${caseItem.violation_id}?auth_token=${getModel().auth_token}`,
					)
					await axios.post(url)
					successCount++
				} catch (error) {
					console.error(
						`Failed to calculate pricing for case ${caseItem.case_id}:`,
						error,
					)
					const errorMessage =
						error.response && error.response.data && error.response.data.error
							? error.response.data.error
							: 'Unknown error'
					errors.push(`Case ${caseItem.case_id}: ${errorMessage}`)
				}
			}

			if (errors.length > 0) {
				setSaveError(
					`Pricing calculated for ${successCount} case(s), but ${errors.length} failed:\n${errors.join('\n')}`,
				)
			} else {
				setSaveSuccess(
					`Successfully calculated pricing for ${successCount} case(s)!`,
				)
			}

			// Refetch cases from backend to get updated pricing
			if (refetchCases) {
				await refetchCases()
			}
		} catch (error) {
			console.error('Failed to calculate pricing:', error)
			setSaveError('Failed to calculate pricing. Please try again.')
		} finally {
			setCalculatingPricing(false)
		}
	}

	const getModifiedPayload = () => {
		const organizations = []
		const violations = []
		const originalById = new Map(
			originalCases.map((c) => [c.violation_id, c]),
		)
		const groups = groupCasesByOrg(editedCases)

		groups.forEach((group) => {
			// Diff org-level fields once per group (representative = first violation)
			const representative = group[0]
			const representativeOriginal = originalById.get(representative.violation_id)
			if (representativeOriginal) {
				const orgChanges = {}
				let hasOrgChanges = false
				ORG_FIELDS.forEach((field) => {
					const diff = diffField(
						field,
						representativeOriginal[field],
						representative[field],
					)
					if (diff.changed) {
						const payloadKey =
							field === 'company_number' ? 'company_cvr' : field
						orgChanges[payloadKey] = diff.value
						hasOrgChanges = true
					}
				})

				if (hasOrgChanges) {
					organizations.push({
						counterparty_organization_id:
							representative.counterparty_organization_id || null,
						digital_property_id: representative.counterparty_organization_id
							? null
							: representative.digital_property_id || null,
						...orgChanges,
					})
				}
			}

			group.forEach((editedCase) => {
				const originalCase = originalById.get(editedCase.violation_id)
				if (!originalCase) return

				const violationChanges = {}
				let hasViolationChanges = false
				VIOLATION_FIELDS.forEach((field) => {
					const diff = diffField(
						field,
						originalCase[field],
						editedCase[field],
					)
					if (diff.changed) {
						violationChanges[field] = diff.value
						hasViolationChanges = true
					}
				})

				if (hasViolationChanges) {
					violations.push({
						violation_id: editedCase.violation_id,
						...violationChanges,
					})
				}
			})
		})

		return { organizations, violations }
	}

	const validateCases = () => {
		const invalidCases = []
		editedCases.forEach((caseItem) => {
			const missingFields = []
			if (!caseItem.currency || caseItem.currency.trim() === '') {
				missingFields.push('Currency')
			}
			if (
				!caseItem.license_price ||
				caseItem.license_price === '' ||
				caseItem.license_price === null
			) {
				missingFields.push('License Price')
			}
			if (!caseItem.company_name || caseItem.company_name.trim() === '') {
				missingFields.push('Company')
			}
			if (missingFields.length > 0) {
				invalidCases.push({
					caseId: caseItem.case_id,
					fields: missingFields,
				})
			}
		})
		return invalidCases
	}

	const getContactErrors = () => {
		const errors = []
		const groups = groupCasesByOrg(editedCases)
		groups.forEach((group) => {
			const label =
				group[0].company_name || `(new organization, digital property ${group[0].digital_property_id})`
			;(group[0].emails || []).forEach((email) => {
				const err = validateEmail(email)
				if (err) errors.push(`${label}: email "${email}" — ${err}`)
			})
			;(group[0].phones || []).forEach((phone) => {
				const err = validatePhone(phone)
				if (err) errors.push(`${label}: phone "${phone}" — ${err}`)
			})
		})
		return errors
	}

	const handleSave = async () => {
		// Validate required fields
		const invalidCases = validateCases()
		if (invalidCases.length > 0) {
			const errorMessage = invalidCases
				.map((c) => `Case ${c.caseId}: Missing ${c.fields.join(', ')}`)
				.join('\n')
			setSaveError(`Please fill in all required fields:\n${errorMessage}`)
			return
		}

		// Validate contact info (emails/phones)
		const contactErrors = getContactErrors()
		if (contactErrors.length > 0) {
			setSaveError(`Please fix contact info:\n${contactErrors.join('\n')}`)
			return
		}

		const payload = getModifiedPayload()
		const changeCount =
			payload.organizations.length + payload.violations.length

		if (changeCount === 0) {
			setSaveSuccess('No changes to save.')
			return
		}

		setSaving(true)
		setSaveError(null)
		setSaveSuccess(null)

		try {
			const url = getApiPath(
				`/cases/export/validate-exports?auth_token=${getModel().auth_token}`,
			)
			await axios.patch(url, payload)

			setSaveSuccess(
				`Successfully saved ${payload.organizations.length} organization(s) and ${payload.violations.length} violation(s)!`,
			)

			if (refetchCases) {
				await refetchCases()
			}

			setOriginalCases(JSON.parse(JSON.stringify(editedCases)))
		} catch (error) {
			console.error('Failed to save edited cases:', error)
			setSaveError('Failed to save changes. Please try again.')
		} finally {
			setSaving(false)
		}
	}

	const handleSaveAndExport = async () => {
		// Validate required fields
		const invalidCases = validateCases()
		if (invalidCases.length > 0) {
			const errorMessage = invalidCases
				.map((c) => `Case ${c.caseId}: Missing ${c.fields.join(', ')}`)
				.join('\n')
			setSaveError(`Please fill in all required fields:\n${errorMessage}`)
			return
		}

		// Validate contact info (emails/phones)
		const contactErrors = getContactErrors()
		if (contactErrors.length > 0) {
			setSaveError(`Please fix contact info:\n${contactErrors.join('\n')}`)
			return
		}

		const payload = getModifiedPayload()
		const changeCount =
			payload.organizations.length + payload.violations.length

		setSaving(true)
		setSaveError(null)
		setSaveSuccess(null)

		try {
			if (changeCount > 0) {
				const url = getApiPath(
					`/cases/export/validate-exports?auth_token=${getModel().auth_token}`,
				)
				await axios.patch(url, payload)
			}

			// Get the client_id from the first case
			const clientId = cases[0] && cases[0].client_id

			if (!clientId) {
				setSaveError('Client ID not found')
				return
			}

			// Trigger export by posting to /cases/export endpoint
			const exportUrl = getApiPath(
				`/cases/export?client_id=${clientId}&auth_token=${getModel().auth_token}`,
			)

			await axios.post(exportUrl)

			// Show success message
			setSaveSuccess(`Successfully exported case(s)!`)

			// Close dialog
			onClose()
			window.location.reload()
		} catch (error) {
			console.error('Failed to save and export:', error)
			setSaveError('Failed to save and export. Please try again.')
		} finally {
			setSaving(false)
		}
	}

	if (!open) {
		return null
	}

	// Custom modal styles for wider display
	const modalContentStyle = {
		background: '#fff',
		borderRadius: 8,
		width: '95%',
		boxShadow:
			'0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
		maxHeight: '85vh',
		display: 'flex',
		flexDirection: 'column',
	}

	return (
		<div
			role='dialog'
			aria-modal='true'
			className='modal-overlay'
			onClick={(e) => e.target === e.currentTarget && onClose()}
			style={{
				position: 'fixed',
				inset: 0,
				background: 'rgba(0,0,0,0.5)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 1050,
				padding: 16,
			}}
		>
			<div className='modal-content' style={modalContentStyle}>
				<div
					className='modal-header'
					style={{
						padding: '16px 20px',
						borderBottom: '1px solid #eee',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<h3 style={{ margin: 0 }}>Validate Cases - {clientName}</h3>
					<button
						aria-label='Close'
						onClick={onClose}
						style={{
							border: 'none',
							background: 'transparent',
							fontSize: 22,
							lineHeight: 1,
							cursor: 'pointer',
						}}
					>
						×
					</button>
				</div>
				<div
					className='modal-body'
					style={{ padding: 20, overflow: 'auto', flex: 1 }}
				>
					{loading && renderLoading()}
					{error && renderError(error)}
					{saveError && renderError(saveError)}
					{saveSuccess && renderSuccess(saveSuccess)}
					{!loading && !error && cases.length === 0 && renderNoCases()}
					{!loading &&
						!error &&
						cases.length > 0 &&
						renderCasesCards(editedCases, handleCellChange, handleMarkAsLost)}
				</div>
				{!loading && !error && cases.length > 0 && (
					<div
						style={{
							padding: '16px 20px',
							borderTop: '1px solid #eee',
							display: 'flex',
							justifyContent: 'space-between',
							gap: '10px',
						}}
					>
						<div style={{ display: 'flex', gap: '10px' }}>
							<Button
								onClick={onClose}
								variant='outlined'
								disabled={saving || calculatingPricing}
							>
								Cancel
							</Button>
							<Button
								onClick={handleCalculatePricing}
								variant='outlined'
								color='default'
								disabled={saving || calculatingPricing}
							>
								{calculatingPricing ? 'Calculating...' : 'Recalculate Pricing'}
							</Button>
						</div>
						<div style={{ display: 'flex', gap: '10px' }}>
							<Button
								onClick={handleSave}
								variant='outlined'
								color='primary'
								disabled={saving || calculatingPricing}
							>
								{saving ? 'Saving...' : 'Save Changes'}
							</Button>
							<Button
								onClick={handleSaveAndExport}
								variant='contained'
								color='primary'
								disabled={saving || calculatingPricing}
							>
								{saving ? 'Processing...' : 'Save & Export'}
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

function renderLoading() {
	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '400px',
			}}
		>
			<Loading />
		</div>
	)
}

function renderError(error) {
	return (
		<div style={{ padding: '20px', textAlign: 'center' }}>
			<h3 style={{ color: '#d32f2f' }}>Error</h3>
			<p style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>{error}</p>
		</div>
	)
}

function renderSuccess(message) {
	return (
		<div
			style={{
				padding: '16px 20px',
				backgroundColor: '#4caf50',
				color: '#fff',
				marginBottom: '16px',
				borderRadius: '4px',
			}}
		>
			<p style={{ margin: 0, fontWeight: 'bold' }}>{message}</p>
		</div>
	)
}

function renderNoCases() {
	return (
		<div style={{ padding: '20px', textAlign: 'center' }}>
			<h3>No cases found</h3>
			<p>There are no cases ready for export for this client.</p>
		</div>
	)
}

// Define these components at module level to prevent re-creation on each render
const EditableCell = ({
	value,
	field,
	violationId,
	type = 'text',
	width = 120,
	handleCellChange,
	required = false,
}) => {
	const isEmpty = required && (!value || value.toString().trim() === '')

	return (
		<TextField
			value={value || ''}
			onChange={(e) => handleCellChange(violationId, field, e.target.value)}
			type={type}
			size='small'
			fullWidth
			required={required}
			error={isEmpty}
			style={{ width }}
			inputProps={{ style: { fontSize: '0.875rem', padding: '4px 8px' } }}
		/>
	)
}

const EditableMultiCell = ({
	values,
	field,
	violationId,
	type = 'text',
	width = 180,
	handleCellChange,
	validate,
}) => {
	const list = Array.isArray(values) ? values : []
	const updateAt = (index, newValue) => {
		const next = list.slice()
		next[index] = newValue
		handleCellChange(violationId, field, next)
	}
	const removeAt = (index) => {
		const next = list.slice()
		next.splice(index, 1)
		handleCellChange(violationId, field, next)
	}
	const addEntry = () => {
		handleCellChange(violationId, field, [...list, ''])
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 4, width }}>
			{list.length === 0 ? (
				<Button size='small' onClick={addEntry} style={{ fontSize: '0.75rem' }}>
					+ add
				</Button>
			) : (
				<>
					{list.map((v, i) => {
						const error = validate ? validate(v) : null
						return (
							<div
								key={i}
								style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
							>
								<div
									style={{ display: 'flex', gap: 4, alignItems: 'center' }}
								>
									<TextField
										value={v || ''}
										onChange={(e) => updateAt(i, e.target.value)}
										type={type}
										size='small'
										error={!!error}
										style={{ flex: 1 }}
										inputProps={{
											style: { fontSize: '0.875rem', padding: '4px 8px' },
										}}
									/>
									<Button
										size='small'
										onClick={() => removeAt(i)}
										style={{
											minWidth: 24,
											padding: '0 4px',
											fontSize: '0.75rem',
										}}
									>
										×
									</Button>
								</div>
								{error && (
									<span
										style={{
											fontSize: '0.7rem',
											color: '#d32f2f',
											paddingLeft: 2,
										}}
									>
										{error}
									</span>
								)}
							</div>
						)
					})}
					<Button
						size='small'
						onClick={addEntry}
						style={{ fontSize: '0.75rem', alignSelf: 'flex-start' }}
					>
						+ add
					</Button>
				</>
			)}
		</div>
	)
}

const EditableCheckbox = ({ value, field, violationId, handleCellChange }) => (
	<Checkbox
		checked={!!value}
		onChange={(e) => handleCellChange(violationId, field, e.target.checked)}
		color='primary'
		size='small'
	/>
)

const EditableSelect = ({
	value,
	field,
	violationId,
	handleCellChange,
	options,
	width = 150,
}) => (
	<Select
		value={value || ''}
		onChange={(e) => handleCellChange(violationId, field, e.target.value)}
		size='small'
		fullWidth
		style={{ minWidth: width, fontSize: '0.875rem' }}
	>
		{options.map((option) => {
			const value = typeof option === 'string' ? option : option.value
			const label = typeof option === 'string' ? option : option.label
			return (
				<MenuItem key={value} value={value}>
					{label}
				</MenuItem>
			)
		})}
	</Select>
)

const numericFieldWidth = 100

const OrgField = ({ label, children }) => (
	<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		<span style={{ fontSize: '0.7rem', color: '#5f6368', fontWeight: 600 }}>
			{label}
		</span>
		{children}
	</div>
)

const ORG_FIELDS = new Set([
	'counterparty_type',
	'company_number',
	'company_name',
	'address',
	'zip',
	'city',
	'country',
	'emails',
	'phones',
])

const VIOLATION_FIELDS = [
	'violation_type',
	'market',
	'soft_claim',
	'currency',
	'license_price',
	'compensation',
	'further_damages',
	'administrative_fee',
	'cease_and_desist_fee',
	'reminder_one_fee',
	'reminder_two_fee',
	'final_warning_fee',
	'manipulated_fee',
	'total',
	'comment',
	'internal_comment',
	'allocation_notes',
	'manipulated',
	'pdf',
]

const BOOLEAN_FIELDS = new Set(['soft_claim', 'manipulated', 'pdf'])
const NUMERIC_FIELDS = new Set([
	'license_price',
	'compensation',
	'further_damages',
	'administrative_fee',
	'collection_fee',
	'cease_and_desist_fee',
	'reminder_one_fee',
	'reminder_two_fee',
	'final_warning_fee',
	'manipulated_fee',
	'total',
])
const ARRAY_FIELDS = new Set(['emails', 'phones'])

const getOrgKey = (c) =>
	c.counterparty_organization_id
		? `org_${c.counterparty_organization_id}`
		: `digital_property_${c.digital_property_id}`

const groupCasesByOrg = (cases) => {
	const groups = new Map()
	cases.forEach((c) => {
		const key = getOrgKey(c)
		if (!groups.has(key)) groups.set(key, [])
		groups.get(key).push(c)
	})
	return groups
}

const diffField = (field, originalValue, editedValue) => {
	if (ARRAY_FIELDS.has(field)) {
		const normOrig = dedupStrings(originalValue)
		const normEdit = dedupStrings(editedValue)
		if (JSON.stringify(normOrig) !== JSON.stringify(normEdit)) {
			return { changed: true, value: normEdit }
		}
		return { changed: false }
	}
	if (BOOLEAN_FIELDS.has(field)) {
		const normOrig = !!originalValue
		const normEdit = !!editedValue
		if (normOrig !== normEdit) return { changed: true, value: normEdit }
		return { changed: false }
	}
	if (NUMERIC_FIELDS.has(field)) {
		const normOrig =
			originalValue === null ||
			originalValue === undefined ||
			originalValue === ''
				? null
				: Number(originalValue)
		const normEdit =
			editedValue === null || editedValue === undefined || editedValue === ''
				? null
				: Number(editedValue)
		if (normOrig !== normEdit) return { changed: true, value: normEdit }
		return { changed: false }
	}
	const normOrig =
		originalValue === null ||
		originalValue === undefined ||
		originalValue === ''
			? null
			: originalValue
	const normEdit =
		editedValue === null || editedValue === undefined || editedValue === ''
			? null
			: editedValue
	if (normOrig !== normEdit) return { changed: true, value: editedValue }
	return { changed: false }
}

const OrgCard = ({ group, handleCellChange, handleMarkAsLost }) => {
	const representativeId = group[0].violation_id
	const columnHeaderStyle = {
		fontWeight: 600,
		backgroundColor: '#f5f5f5',
		whiteSpace: 'nowrap',
		padding: '6px 10px',
		fontSize: '0.8rem',
	}
	return (
		<div
			style={{
				border: '1px solid #dadce0',
				borderRadius: 8,
				marginBottom: 16,
				backgroundColor: '#fff',
				boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
				overflow: 'hidden',
			}}
		>
			<div
				style={{
					padding: '16px 20px',
					backgroundColor: '#f8f9fa',
					borderBottom: '1px solid #dadce0',
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'baseline',
						marginBottom: 12,
					}}
				>
					<div
						style={{ fontWeight: 700, fontSize: '1rem', color: '#0d47a1' }}
					>
						{group[0].company_name || '(new organization)'}
					</div>
					<div style={{ fontSize: '0.75rem', color: '#5f6368' }}>
						{group.length} violation{group.length === 1 ? '' : 's'}
					</div>
				</div>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
						gap: '10px 16px',
						alignItems: 'start',
					}}
				>
					<OrgField label='Company Name *'>
						<EditableCell
							value={group[0].company_name}
							field='company_name'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
							required={true}
						/>
					</OrgField>
					<OrgField label='Company Number'>
						<EditableCell
							value={group[0].company_number}
							field='company_number'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
						/>
					</OrgField>
					<OrgField label='CP Type'>
						<EditableSelect
							value={group[0].counterparty_type}
							field='counterparty_type'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
							options={counterpartyTypes}
						/>
					</OrgField>
					<OrgField label='Country'>
						<EditableSelect
							value={group[0].country}
							field='country'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
							options={markets.map((m) => ({
								value: m.code,
								label: m.fullName,
							}))}
						/>
					</OrgField>
					<OrgField label='Address'>
						<EditableCell
							value={group[0].address}
							field='address'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
						/>
					</OrgField>
					<OrgField label='Zip'>
						<EditableCell
							value={group[0].zip}
							field='zip'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
						/>
					</OrgField>
					<OrgField label='City'>
						<EditableCell
							value={group[0].city}
							field='city'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
						/>
					</OrgField>
					<OrgField label='Emails'>
						<EditableMultiCell
							values={group[0].emails}
							field='emails'
							violationId={representativeId}
							type='email'
							width='100%'
							handleCellChange={handleCellChange}
							validate={validateEmail}
						/>
					</OrgField>
					<OrgField label='Phones'>
						<EditableMultiCell
							values={group[0].phones}
							field='phones'
							violationId={representativeId}
							width='100%'
							handleCellChange={handleCellChange}
							validate={validatePhone}
						/>
					</OrgField>
				</div>
			</div>
			<div style={{ overflowX: 'auto' }}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell style={columnHeaderStyle}>Case ID</TableCell>
							<TableCell style={columnHeaderStyle}>Violation Type</TableCell>
							<TableCell style={columnHeaderStyle}>Market</TableCell>
							<TableCell style={columnHeaderStyle}>URL</TableCell>
							<TableCell style={columnHeaderStyle}>Soft Claim</TableCell>
							<TableCell style={columnHeaderStyle}>Currency *</TableCell>
							<TableCell style={columnHeaderStyle}>License Price *</TableCell>
							<TableCell style={columnHeaderStyle}>Compensation</TableCell>
							<TableCell style={columnHeaderStyle}>Further Damages</TableCell>
							<TableCell style={columnHeaderStyle}>Administrative Fee</TableCell>
							<TableCell style={columnHeaderStyle}>Cease and Desist Fee</TableCell>
							<TableCell style={columnHeaderStyle}>Reminder One Fee</TableCell>
							<TableCell style={columnHeaderStyle}>Final Warning Fee</TableCell>
							<TableCell style={columnHeaderStyle}>Manipulated Fee</TableCell>
							<TableCell style={columnHeaderStyle}>Collection Fee</TableCell>
							<TableCell style={columnHeaderStyle}>Total</TableCell>
							<TableCell style={columnHeaderStyle}>Comment</TableCell>
							<TableCell style={columnHeaderStyle}>Internal Comment</TableCell>
							<TableCell style={columnHeaderStyle}>Allocation Notes</TableCell>
							<TableCell style={columnHeaderStyle}>Manipulated</TableCell>
							<TableCell style={columnHeaderStyle}>PDF</TableCell>
							<TableCell style={columnHeaderStyle}>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{group.map((caseItem) => (
							<TableRow key={caseItem.violation_id}>
								<TableCell style={{ whiteSpace: 'nowrap' }}>
									{caseItem.case_id}
								</TableCell>
								<TableCell>
									<EditableSelect
										value={caseItem.violation_type}
										field='violation_type'
										violationId={caseItem.violation_id}
										width={150}
										handleCellChange={handleCellChange}
										options={['Commercial', 'Editorial']}
									/>
								</TableCell>
								<TableCell>
									<EditableSelect
										value={caseItem.market}
										field='market'
										violationId={caseItem.violation_id}
										width={120}
										handleCellChange={handleCellChange}
										options={markets.map((m) => ({
											value: m.code,
											label: m.fullName,
										}))}
									/>
								</TableCell>
								<TableCell style={{ maxWidth: '400px' }}>
									<a
										style={{
											color: '#1976d2',
											display: 'block',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}
										href={caseItem.url_raw}
										target='_blank'
										rel='noopener noreferrer'
										title={caseItem.url_raw}
									>
										{caseItem.url_raw}
									</a>
								</TableCell>
								<TableCell style={{ textAlign: 'center' }}>
									<EditableCheckbox
										value={caseItem.soft_claim}
										field='soft_claim'
										violationId={caseItem.violation_id}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.currency}
										field='currency'
										violationId={caseItem.violation_id}
										width={80}
										handleCellChange={handleCellChange}
										required={true}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.license_price}
										field='license_price'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
										required={true}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.compensation}
										field='compensation'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.further_damages}
										field='further_damages'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.administrative_fee}
										field='administrative_fee'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.cease_and_desist_fee}
										field='cease_and_desist_fee'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.reminder_one_fee}
										field='reminder_one_fee'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.final_warning_fee}
										field='final_warning_fee'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.manipulated_fee}
										field='manipulated_fee'
										violationId={caseItem.violation_id}
										type='number'
										width={numericFieldWidth}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>{caseItem.collection_fee}</TableCell>
								<TableCell>{caseItem.total}</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.comment}
										field='comment'
										violationId={caseItem.violation_id}
										width={200}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.internal_comment}
										field='internal_comment'
										violationId={caseItem.violation_id}
										width={200}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCell
										value={caseItem.allocation_notes}
										field='allocation_notes'
										violationId={caseItem.violation_id}
										width={200}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell style={{ textAlign: 'center' }}>
									<EditableCheckbox
										value={caseItem.manipulated}
										field='manipulated'
										violationId={caseItem.violation_id}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<EditableCheckbox
										value={caseItem.pdf}
										field='pdf'
										violationId={caseItem.violation_id}
										handleCellChange={handleCellChange}
									/>
								</TableCell>
								<TableCell>
									<Button
										variant='outlined'
										color='secondary'
										size='small'
										onClick={() => handleMarkAsLost(caseItem.violation_id)}
										style={{ whiteSpace: 'nowrap' }}
									>
										Mark as Lost
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}

function renderCasesCards(cases, handleCellChange, handleMarkAsLost) {
	return (
		<div>
			<p style={{ marginBottom: '16px', fontWeight: 'bold' }}>
				Total Cases: {cases.length}
			</p>
			<div
				style={{
					maxHeight: 'calc(85vh - 250px)',
					overflowY: 'auto',
					padding: '4px',
				}}
			>
				{Array.from(groupCasesByOrg(cases).entries()).map(([orgKey, group]) => (
					<OrgCard
						key={orgKey}
						group={group}
						handleCellChange={handleCellChange}
						handleMarkAsLost={handleMarkAsLost}
					/>
				))}
			</div>
		</div>
	)
}
